import config from "./config";
import { CognitoUserPool, AuthenticationDetails, CognitoUser, CognitoUserSession } from "amazon-cognito-identity-js";

import { SessionData, UserCheckResult } from "../types/authTypes";
import { allCompaniesInfo, CompanyInfo } from "../types/companyTypes";

const userPool = new CognitoUserPool({
  UserPoolId: "us-east-1_1epcyobz1",
  ClientId: "2haiep5j3460kffbvv3mfhbvib",
});

const login = (email: string, password: string): Promise<SessionData> => {
  const authenticationDetails = new AuthenticationDetails({
    Username: email,
    Password: password,
  });

  const cognitoUser = new CognitoUser({
    Username: email,
    Pool: userPool,
  });

  return new Promise((resolve, reject) => {
    cognitoUser.authenticateUser(authenticationDetails, {
      onSuccess: (result) => {
        const sessionData: SessionData = {
          signInUserSession: {
            idToken: result.getIdToken().getJwtToken(),
            accessToken: result.getAccessToken().getJwtToken(),
            refreshToken: result.getRefreshToken().getToken(),
          },
          username: cognitoUser.getUsername(),
          newPassRequired: false,
          cognitoId: result.getIdToken().payload.sub,
        };
        resolve(sessionData);
      },
      onFailure: (err) => {
        reject(err);
      },
      newPasswordRequired: () => {
        resolve({
          signInUserSession: {
            idToken: "",
            accessToken: "",
            refreshToken: "",
          },
          username: cognitoUser.getUsername(),
          newPassRequired: true,
          cognitoId: "",
        });
      },
    });
  });
};

const getTokens = (): Promise<CognitoUserSession> => {
  return new Promise((resolve, reject) => {
    const currentUser = userPool.getCurrentUser();
    if (!currentUser) {
      window.localStorage.clear();
      reject("Current user not found in pool.");
      window.location.href = "/";
      return;
    }

    currentUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session) {
        reject(err || new Error("Session not found"));
      } else {
        resolve(session);
      }
    });
  });
};

export const getAccessToken = async (): Promise<string> => {
  try {
    const session = await getTokens();
    return session.getAccessToken().getJwtToken();
  } catch (error) {
    throw new Error(String(error));
  }
};

const userCheck = async (email: string): Promise<UserCheckResult> => {
  try {
    const response = await fetch(`${config.BASE_URL}/api/user-check/${email}`);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    return data;
  } catch (error) {
    console.error("Error during user check:", error);
    throw error;
  }
};

const getUserCompany = async (company_id: number | string, token?: string): Promise<CompanyInfo> => {
  try {
    const response = await fetch(`${import.meta.env.VITE_API_SERVICES}/api/v1/company/getUserCompany?company_id=${company_id || ""}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Network response was not ok: ${response.statusText}`);
    }

    const data: CompanyInfo = await response.json();

    if (!data) {
      throw new Error("Received null response from server");
    }

    return data;
  } catch (err) {
    console.error("Fetch error:", err);
    throw err;
  }
};

const getUserCompanies = async (): Promise<allCompaniesInfo[]> => {
  try {
    const token = await getAccessToken();
    const response = await fetch(`${config.BASE_URL}/api/users/getUserCompanies`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Network response was not ok: ${response.statusText}`);
    }

    const data = await response.json();

    if (data === null) {
      throw new Error("Received null response from server");
    }

    return data;
  } catch (err) {
    console.error("Fetch error:", err);
    throw err;
  }
};
export { login, getUserCompany, userCheck, getUserCompanies };
